import type { CraftingGrid, IngredientSpec, ItemId, VanillaRecipe } from '@/types/domain';
import { VANILLA_RECIPES } from '@/data/vanillaRecipes';

export interface VanillaMatch {
  matched: boolean;
  recipe?: VanillaRecipe;
  result?: { item: ItemId; count: number };
}

function specAccepts(spec: IngredientSpec, id: ItemId): boolean {
  if (typeof spec === 'string') return spec === id;
  return spec.items.includes(id);
}

function isSpecTag(spec: IngredientSpec): spec is { tag: string; items: ItemId[] } {
  return typeof spec === 'object' && spec !== null && 'items' in spec;
}

// ---- shapeless multiset matching ----
function multisetMatch(ingredientIds: ItemId[], specs: IngredientSpec[]): boolean {
  if (ingredientIds.length !== specs.length) return false;
  const pool = new Map<ItemId, number>();
  for (const id of ingredientIds) pool.set(id, (pool.get(id) || 0) + 1);
  // Greedy: assign exact specs first, then tag specs.
  const remaining: IngredientSpec[] = [];
  for (const spec of specs) {
    if (typeof spec === 'string') {
      const n = pool.get(spec) || 0;
      if (n <= 0) return false;
      pool.set(spec, n - 1);
    } else {
      remaining.push(spec);
    }
  }
  const leftoverIds = [...pool.entries()].flatMap(([id, n]) => Array(n).fill(id));
  // For tag specs, try to satisfy each from leftoverIds.
  const used = new Array(leftoverIds.length).fill(false);
  for (const spec of remaining) {
    let placed = false;
    for (let i = 0; i < leftoverIds.length; i++) {
      if (used[i]) continue;
      if (specAccepts(spec, leftoverIds[i])) {
        used[i] = true;
        placed = true;
        break;
      }
    }
    if (!placed) return false;
  }
  // Counts already match (lengths equal), so placing every tag spec consumes all leftovers.
  return true;
}

// ---- shaped bounding-box matching ----
function toMatrix(grid: CraftingGrid): (ItemId | null)[][] {
  const m: (ItemId | null)[][] = [[null, null, null], [null, null, null], [null, null, null]];
  for (let i = 0; i < 9; i++) {
    const r = Math.floor(i / 3);
    const c = i % 3;
    m[r][c] = grid[i] ? grid[i]!.itemId : null;
  }
  return m;
}

function trim(m: (ItemId | null)[][]): (ItemId | null)[][] {
  let minR = 3, maxR = -1, minC = 3, maxC = -1;
  for (let r = 0; r < 3; r++)
    for (let c = 0; c < 3; c++)
      if (m[r][c] !== null) {
        minR = Math.min(minR, r); maxR = Math.max(maxR, r);
        minC = Math.min(minC, c); maxC = Math.max(maxC, c);
      }
  if (maxR < 0) return [];
  const box: (ItemId | null)[][] = [];
  for (let r = minR; r <= maxR; r++) {
    const row: (ItemId | null)[] = [];
    for (let c = minC; c <= maxC; c++) row.push(m[r][c]);
    box.push(row);
  }
  return box;
}

function patternMatrix(pattern: string[], key: Record<string, IngredientSpec>): (string | null)[][] {
  const m: (string | null)[][] = [[null, null, null], [null, null, null], [null, null, null]];
  for (let r = 0; r < 3; r++) {
    const row = pattern[r] || '';
    for (let c = 0; c < 3; c++) {
      const ch = row[c] || ' ';
      if (ch === ' ' || !(ch in key)) m[r][c] = null;
      else m[r][c] = ch;
    }
  }
  return m;
}

function matchShaped(grid: CraftingGrid, recipe: VanillaRecipe): boolean {
  if (!recipe.pattern || !recipe.key) return false;
  const g = trim(toMatrix(grid));
  const p = trim(patternMatrix(recipe.pattern, recipe.key));
  if (g.length !== p.length) return false;
  if (g.length === 0) return false;
  if (g[0].length !== p[0].length) return false;
  const assigned: Record<string, ItemId> = {};
  for (let r = 0; r < g.length; r++) {
    for (let c = 0; c < g[0].length; c++) {
      const ch = p[r][c];
      const id = g[r][c];
      if (ch === null && id === null) continue;
      if (ch === null || id === null) return false;
      const spec = recipe.key[ch];
      if (!specAccepts(spec, id)) return false;
      // Same key char must use the same material (vanilla rule).
      if (isSpecTag(spec)) {
        if (assigned[ch] === undefined) assigned[ch] = id;
        else if (assigned[ch] !== id) return false;
      }
    }
  }
  return true;
}

export class VanillaRecipeEngine {
  constructor(private recipes: VanillaRecipe[] = VANILLA_RECIPES) {}

  match(grid: CraftingGrid): VanillaMatch {
    const filled = grid.some((c) => c !== null);
    if (!filled) return { matched: false };

    const ingredientIds: ItemId[] = [];
    for (const cell of grid) if (cell) ingredientIds.push(cell.itemId);

    for (const recipe of this.recipes) {
      if (recipe.type === 'shaped') {
        if (matchShaped(grid, recipe)) return { matched: true, recipe, result: recipe.result };
      } else {
        if (recipe.ingredients && multisetMatch(ingredientIds, recipe.ingredients))
          return { matched: true, recipe, result: recipe.result };
      }
    }
    return { matched: false };
  }
}

export const vanillaRecipeEngine = new VanillaRecipeEngine();
